import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectScreen, selectElement } from './utils/state/canvasSlice';
import {
    moveElementToScreen,
    moveElementToLocation,
    moveElementToLayer,
    pushElementLayerUp,
    pushElementLayerDown,
    updateScreenUIElement,
    deleteUIElement,
    deleteScreen,
} from './utils/state/screensSlice';
import {
    Camera,
    ChevronDown,
    ChevronRight,
    Monitor,
    Layers,
    MapPin,
    Square,
    Type,
    Image,
    MousePointer,
    Keyboard,
    QrCode,
    Eye,
    Grid3X3,
    Play,
    Users,
    Palette,
    ArrowUp,
    ArrowDown,
    MoreVertical,
    Trash2,
    Plus,
    Hash,
} from 'lucide-react';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import { deleteElementWithUndo } from './utils/undoRedo/actionHelpers';
import { createScreen } from './utils/state/screensSlice';
import { addScreenAtCenter } from './utils/state/canvasSlice';

interface HierarchyPanelProps {
    screens: ScreenDefinition[];
    activeTab?: string;
    onDeleteScreen?: (screenId: string) => void;
    onCreateScreen?: (screen: ScreenDefinition) => void;
}

interface HierarchyState {
    expandedScreens: Set<string>;
    expandedLayers: Set<string>;
    expandedLocations: Set<string>;
    expandedGroups: { [groupId: string]: boolean };
    isDragging: boolean;
    draggedElementId?: string;
    dragHoverTarget?: string; // Track what's being hovered during drag
    contextMenu: {
        visible: boolean;
        x: number;
        y: number;
        elementId?: string;
        screenId?: string;
    };
}

// Element type icons mapping
const ELEMENT_ICONS: { [key in UIElementType]: any } = {
    text: Type,
    dynamicText: Type,
    button: MousePointer,
    image: Image,
    input: Keyboard,
    outputPreview: Play,
    currentSessionQR: QrCode,
    paymentQR: QrCode,
    galleryGrid: Grid3X3,
    mediaGrid: Grid3X3,
    sessionGrid: Grid3X3,
    liveView: Eye,
    selectedQR: QrCode,
    group: Users,
    artworkSelector: Palette,
    doubleExposureSelector: Palette,
    codeScanner: Camera,
    phoneKeypad: Hash,
};

// Location labels mapping
const LOCATION_LABELS: { [key: string]: string } = {
    'leading-top': 'Top Left',
    'center-top': 'Top Center',
    'trailing-top': 'Top Right',
    'leading-center': 'Middle Left',
    'center-center': 'Middle Center',
    'trailing-center': 'Middle Right',
    'leading-bottom': 'Bottom Left',
    'center-bottom': 'Bottom Center',
    'trailing-bottom': 'Bottom Right',
};

export default function HierarchyPanel({
    screens,
    activeTab = 'main',
    onDeleteScreen,
    onCreateScreen,
}: HierarchyPanelProps) {
    const dispatch = useDispatch();
    const {
        selectedScreenId,
        selectedElementId,
        isDragFromPalette,
        draggedElementType,
    } = useSelector((state: any) => state.canvas);

    const contextMenuRef = useRef<HTMLDivElement>(null);

    const [hierarchyState, setHierarchyState] = useState<HierarchyState>({
        expandedScreens: new Set([selectedScreenId].filter(Boolean)),
        expandedLayers: new Set(), // Will be populated with default expansions below
        expandedLocations: new Set(), // Will be populated with default expansions below
        expandedGroups: {},
        isDragging: false,
        draggedElementId: undefined,
        dragHoverTarget: undefined,
        contextMenu: {
            visible: false,
            x: 0,
            y: 0,
            elementId: undefined,
            screenId: undefined,
        },
    });

    // Delete confirmation dialog state
    const [deleteDialog, setDeleteDialog] = useState<{
        isOpen: boolean;
        type: 'element' | 'screen';
        elementId?: string;
        screenId?: string;
        title: string;
        message: string;
    }>({
        isOpen: false,
        type: 'element',
        title: '',
        message: '',
    });

    // Auto-expand hierarchy when element is selected
    useEffect(() => {
        if (selectedElementId && selectedScreenId) {
            // Find the selected element
            const selectedScreen = screens.find(
                (screen) => screen.id === selectedScreenId,
            );
            const selectedElementObj = selectedScreen?.elements?.find(
                (el) => el.id === selectedElementId,
            );

            if (selectedElementObj) {
                setHierarchyState((prevState) => {
                    const newState = { ...prevState };

                    // Expand the screen
                    newState.expandedScreens = new Set(
                        prevState.expandedScreens,
                    );
                    newState.expandedScreens.add(selectedScreenId);

                    // Expand the layer containing the element
                    const layer = selectedElementObj.layer || 0;
                    const layerKey = `${selectedScreenId}-layer-${layer}`;
                    newState.expandedLayers = new Set(prevState.expandedLayers);
                    newState.expandedLayers.add(layerKey);

                    // Expand the location containing the element
                    const locationKey = `${selectedElementObj.horizontalLocation}-${selectedElementObj.verticalLocation}`;
                    const locationExpandKey = `${selectedScreenId}-${locationKey}`;
                    newState.expandedLocations = new Set(
                        prevState.expandedLocations,
                    );
                    newState.expandedLocations.add(locationExpandKey);

                    return newState;
                });

                // Scroll to the selected element after a short delay to allow expansion
                setTimeout(() => {
                    const elementDiv = document.querySelector(
                        `[data-element-id="${selectedElementId}"]`,
                    );
                    if (elementDiv) {
                        elementDiv.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        });
                    }
                }, 150);
            }
        }
    }, [selectedElementId, selectedScreenId, screens]);

    // Group elements by layer and location (excluding grouped elements)
    const getScreenHierarchy = (screen: ScreenDefinition) => {
        const layers: { [layer: number]: { [location: string]: UIElement[] } } =
            {
                0: {},
                1: {},
                2: {},
            };

        // Ensure elements array exists, default to empty array if undefined
        const elements = screen.elements || [];
        elements.forEach((element) => {
            // Skip elements that belong to a group - they'll be rendered under their parent group
            if (element.groupLocation) {
                return;
            }

            const layer = element.layer || 0;
            const locationKey = `${element.horizontalLocation}-${element.verticalLocation}`;

            if (!layers[layer][locationKey]) {
                layers[layer][locationKey] = [];
            }
            layers[layer][locationKey].push(element);
        });

        return layers;
    };

    // Auto-expand only the selected screen's layers and locations that contain elements
    useEffect(() => {
        if (!selectedScreenId) return;

        const selectedScreen = screens.find((s) => s.id === selectedScreenId);
        if (!selectedScreen) return;

        const hierarchy = getScreenHierarchy(selectedScreen);
        const newExpandedLayers = new Set<string>();
        const newExpandedLocations = new Set<string>();

        // Expand layers that have elements for the selected screen only
        Object.keys(hierarchy).forEach((layerKey) => {
            const layerNum = parseInt(layerKey);
            const locations = hierarchy[layerNum];
            const hasElements = Object.values(locations).some(
                (elements) => elements.length > 0,
            );

            if (hasElements) {
                newExpandedLayers.add(`${selectedScreen.id}-layer-${layerNum}`);

                // Also expand locations within this layer that have elements
                Object.keys(locations).forEach((locationKey) => {
                    if (locations[locationKey].length > 0) {
                        newExpandedLocations.add(
                            `${selectedScreen.id}-${locationKey}`,
                        );
                    }
                });
            }
        });

        setHierarchyState((prevState) => ({
            ...prevState,
            expandedScreens: new Set([selectedScreenId].filter(Boolean)),
            expandedLayers: newExpandedLayers,
            expandedLocations: newExpandedLocations,
        }));
    }, [selectedScreenId, screens]); // Run when selected screen changes

    const hideContextMenu = useCallback(() => {
        setHierarchyState((prevState) => ({
            ...prevState,
            contextMenu: {
                ...prevState.contextMenu,
                visible: false,
            },
        }));
    }, []);

    // Hide context menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            if (hierarchyState.contextMenu.visible) {
                hideContextMenu();
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [hierarchyState.contextMenu.visible, hideContextMenu]);

    // Reposition context menu after render to use actual dimensions
    useEffect(() => {
        if (hierarchyState.contextMenu.visible && contextMenuRef.current) {
            const menu = contextMenuRef.current;
            const rect = menu.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const margin = 20;

            let newX = hierarchyState.contextMenu.x;
            let newY = hierarchyState.contextMenu.y;

            // Check if menu overflows right edge
            if (rect.right > viewportWidth - margin) {
                newX = viewportWidth - rect.width - margin;
            }

            // Check if menu overflows bottom edge
            if (rect.bottom > viewportHeight - margin) {
                newY = viewportHeight - rect.height - margin;
            }

            // Ensure menu doesn't go off left/top edges
            newX = Math.max(margin, newX);
            newY = Math.max(margin, newY);

            // Update position if it changed
            if (
                newX !== hierarchyState.contextMenu.x ||
                newY !== hierarchyState.contextMenu.y
            ) {
                setHierarchyState((prevState) => ({
                    ...prevState,
                    contextMenu: {
                        ...prevState.contextMenu,
                        x: newX,
                        y: newY,
                    },
                }));
            }
        }
    }, [
        hierarchyState.contextMenu.visible,
        hierarchyState.contextMenu.x,
        hierarchyState.contextMenu.y,
    ]);

    const toggleScreenExpansion = (screenId: string) => {
        const newExpanded = new Set(hierarchyState.expandedScreens);
        if (newExpanded.has(screenId)) {
            newExpanded.delete(screenId);
        } else {
            newExpanded.add(screenId);
        }
        setHierarchyState({
            ...hierarchyState,
            expandedScreens: newExpanded,
        });
    };

    const toggleLayerExpansion = (layerKey: string) => {
        const newExpanded = new Set(hierarchyState.expandedLayers);
        if (newExpanded.has(layerKey)) {
            newExpanded.delete(layerKey);
        } else {
            newExpanded.add(layerKey);
        }
        setHierarchyState({
            ...hierarchyState,
            expandedLayers: newExpanded,
        });
    };

    const toggleLocationExpansion = (locationKey: string) => {
        const newExpanded = new Set(hierarchyState.expandedLocations);
        if (newExpanded.has(locationKey)) {
            newExpanded.delete(locationKey);
        } else {
            newExpanded.add(locationKey);
        }
        setHierarchyState({
            ...hierarchyState,
            expandedLocations: newExpanded,
        });
    };

    const handleScreenSelect = (screenId: string) => {
        dispatch(selectScreen(screenId));
        dispatch(selectElement(null)); // Clear element selection
    };

    const handleElementSelect = (screenId: string, elementId: string) => {
        dispatch(selectScreen(screenId));
        dispatch(selectElement(elementId));
    };

    // Context menu handlers with edge detection
    const calculateContextMenuPosition = (
        clientX: number,
        clientY: number,
        isScreenMenu: boolean = false,
    ) => {
        const menuWidth = 220; // Increased width estimate
        const elementMenuHeight = 160; // Increased height for element menu (3 items + separators + padding)
        const screenMenuHeight = 80; // Increased height for screen menu (1 item + padding)
        const margin = 20; // Increased margin from edges

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let x = clientX;
        let y = clientY;

        const currentMenuHeight = isScreenMenu
            ? screenMenuHeight
            : elementMenuHeight;

        // More aggressive right edge check
        if (x + menuWidth + margin > viewportWidth) {
            x = Math.max(margin, clientX - menuWidth);
        }

        // More aggressive bottom edge check
        if (y + currentMenuHeight + margin > viewportHeight) {
            y = Math.max(margin, clientY - currentMenuHeight);
        }

        // Ensure menu never goes off screen with larger margins
        x = Math.max(margin, Math.min(x, viewportWidth - menuWidth - margin));
        y = Math.max(
            margin,
            Math.min(y, viewportHeight - currentMenuHeight - margin),
        );

        return { x, y };
    };

    const handleContextMenu = (
        e: React.MouseEvent,
        elementId: string,
        screenId: string,
    ) => {
        e.preventDefault();
        const position = calculateContextMenuPosition(
            e.clientX,
            e.clientY,
            false,
        ); // Element menu

        setHierarchyState({
            ...hierarchyState,
            contextMenu: {
                visible: true,
                x: position.x,
                y: position.y,
                elementId,
                screenId,
            },
        });
    };

    const handleScreenContextMenu = (e: React.MouseEvent, screenId: string) => {
        e.preventDefault();
        const position = calculateContextMenuPosition(
            e.clientX,
            e.clientY,
            true,
        ); // Screen menu

        setHierarchyState({
            ...hierarchyState,
            contextMenu: {
                visible: true,
                x: position.x,
                y: position.y,
                elementId: undefined, // No element, just screen
                screenId,
            },
        });
    };

    const handleLayerUp = () => {
        if (
            hierarchyState.contextMenu.elementId &&
            hierarchyState.contextMenu.screenId
        ) {
            dispatch(
                pushElementLayerUp({
                    elementId: hierarchyState.contextMenu.elementId,
                    screenId: hierarchyState.contextMenu.screenId,
                }),
            );
        }
        hideContextMenu();
    };

    const handleLayerDown = () => {
        if (
            hierarchyState.contextMenu.elementId &&
            hierarchyState.contextMenu.screenId
        ) {
            dispatch(
                pushElementLayerDown({
                    elementId: hierarchyState.contextMenu.elementId,
                    screenId: hierarchyState.contextMenu.screenId,
                }),
            );
        }
        hideContextMenu();
    };

    const handleDeleteElement = () => {
        if (
            hierarchyState.contextMenu.elementId &&
            hierarchyState.contextMenu.screenId
        ) {
            setDeleteDialog({
                isOpen: true,
                type: 'element',
                elementId: hierarchyState.contextMenu.elementId,
                screenId: hierarchyState.contextMenu.screenId,
                title: 'Delete Element',
                message:
                    'Are you sure you want to delete this element? This action cannot be undone.',
            });
        }
        hideContextMenu();
    };

    const confirmDeleteElement = () => {
        if (deleteDialog.elementId && deleteDialog.screenId) {
            // Find the element data for undo support
            const screen = screens.find((s) => s.id === deleteDialog.screenId);
            const element = screen?.elements?.find(
                (e) => e.id === deleteDialog.elementId,
            );

            if (element) {
                // Use undo-enabled delete
                deleteElementWithUndo(
                    dispatch,
                    deleteDialog.elementId,
                    deleteDialog.screenId,
                    element,
                );
            } else {
                // Fallback to regular delete if element not found
                dispatch(
                    deleteUIElement({
                        elementId: deleteDialog.elementId,
                        screenId: deleteDialog.screenId,
                    }),
                );
            }

            // Clear selection if the deleted element was selected
            if (selectedElementId === deleteDialog.elementId) {
                dispatch(selectElement(null));
            }
        }
        setDeleteDialog({
            isOpen: false,
            type: 'element',
            title: '',
            message: '',
        });
    };

    const handleDeleteScreen = () => {
        if (hierarchyState.contextMenu.screenId) {
            setDeleteDialog({
                isOpen: true,
                type: 'screen',
                screenId: hierarchyState.contextMenu.screenId,
                title: 'Delete Screen',
                message:
                    'Are you sure you want to delete this screen? This action cannot be undone.',
            });
        }
        hideContextMenu();
    };

    const confirmDeleteScreen = () => {
        if (deleteDialog.screenId) {
            // Use custom handler if provided (for sharing stations), otherwise use main screens slice
            if (onDeleteScreen && activeTab !== 'main') {
                onDeleteScreen(deleteDialog.screenId);
            } else {
                dispatch(
                    deleteScreen({
                        screenId: deleteDialog.screenId,
                    }),
                );
            }

            // Clear selection if the deleted screen was selected
            if (selectedScreenId === deleteDialog.screenId) {
                dispatch(selectScreen(undefined));
                dispatch(selectElement(null));
            }
        }
        setDeleteDialog({
            isOpen: false,
            type: 'screen',
            title: '',
            message: '',
        });
    };

    const handleCreateScreen = () => {
        const screenId = `screen-${Date.now()}`;
        const newScreen: ScreenDefinition = {
            id: screenId,
            displayProperties: {
                backgroundColor: '#FFFFFF',
                backgroundMediaUrl: '',
                foregroundColor: '#000000',
                buttonColor: '#3B82F6',
                buttonTextColor: '#FFFFFF',
                horizontalPadding: 0,
                verticalPadding: 0,
            },
            stage: {
                type: 'attract' as any,
                metadata: {},
            },
            elements: [],
            navigateDestination: undefined,
            navigateAfterTimeout: undefined,
            resetAfterTimeout: null,
            metadata: {},
        };

        if (onCreateScreen) {
            onCreateScreen(newScreen);
        } else {
            // Fallback to direct dispatch for main experience
            dispatch(createScreen(newScreen));

            // Position the new screen at the center of the canvas
            dispatch(
                addScreenAtCenter({
                    screenId,
                    canvasWidth: 1200, // Default canvas width
                    canvasHeight: 800, // Default canvas height
                }),
            );
        }
    };

    // Drag and drop handlers
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragStart = (elementId: string) => {
        console.log('Drag started for element:', elementId);
        setHierarchyState((prevState) => ({
            ...prevState,
            isDragging: true,
            draggedElementId: elementId,
        }));
    };

    const handleDragEnd = () => {
        setHierarchyState((prevState) => ({
            ...prevState,
            isDragging: false,
            draggedElementId: undefined,
            dragHoverTarget: undefined,
        }));
    };

    const handleDragEnter = (targetId: string) => {
        setHierarchyState((prevState) => ({
            ...prevState,
            dragHoverTarget: targetId,
        }));
    };

    const handleDragLeave = () => {
        setHierarchyState((prevState) => ({
            ...prevState,
            dragHoverTarget: undefined,
        }));
    };

    const handleScreenDrop = (e: React.DragEvent, targetScreenId: string) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            const dragData = JSON.parse(
                e.dataTransfer.getData('application/json'),
            );

            if (
                dragData.type === 'element' &&
                dragData.sourceScreenId !== targetScreenId
            ) {
                dispatch(
                    moveElementToScreen({
                        elementId: dragData.elementId,
                        sourceScreenId: dragData.sourceScreenId,
                        targetScreenId: targetScreenId,
                    }),
                );

                // Update selection to follow the moved element
                dispatch(selectScreen(targetScreenId));
                dispatch(selectElement(dragData.elementId));
            }
        } catch (error) {
            console.warn('Invalid drag data:', error);
        } finally {
            // Always reset drag state
            handleDragEnd();
        }
    };

    const handleLayerDrop = (
        e: React.DragEvent,
        screenId: string,
        layer: number,
    ) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            const dragData = JSON.parse(
                e.dataTransfer.getData('application/json'),
            );

            if (dragData.type === 'element') {
                // If element is from different screen, move it first
                if (dragData.sourceScreenId !== screenId) {
                    dispatch(
                        moveElementToScreen({
                            elementId: dragData.elementId,
                            sourceScreenId: dragData.sourceScreenId,
                            targetScreenId: screenId,
                        }),
                    );
                }

                // Then move to the target layer
                dispatch(
                    moveElementToLayer({
                        elementId: dragData.elementId,
                        screenId: screenId,
                        layer: layer,
                    }),
                );

                // Update selection
                dispatch(selectScreen(screenId));
                dispatch(selectElement(dragData.elementId));
            }
        } catch (error) {
            console.warn('Invalid drag data:', error);
        } finally {
            // Always reset drag state
            handleDragEnd();
        }
    };

    const handleLocationDrop = (
        e: React.DragEvent,
        screenId: string,
        locationKey: string,
        layer?: number,
    ) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            const dragData = JSON.parse(
                e.dataTransfer.getData('application/json'),
            );

            if (dragData.type === 'element') {
                const [horizontalLocation, verticalLocation] =
                    locationKey.split('-');

                // If element is from different screen, move it first
                if (dragData.sourceScreenId !== screenId) {
                    dispatch(
                        moveElementToScreen({
                            elementId: dragData.elementId,
                            sourceScreenId: dragData.sourceScreenId,
                            targetScreenId: screenId,
                        }),
                    );
                }

                // Move to target location
                dispatch(
                    moveElementToLocation({
                        elementId: dragData.elementId,
                        screenId: screenId,
                        horizontalLocation,
                        verticalLocation,
                    }),
                );

                // Clear group location since element is now positioned individually
                dispatch(
                    updateScreenUIElement({
                        screenId: screenId,
                        elementId: dragData.elementId,
                        key: 'groupLocation',
                        value: undefined,
                    }),
                );

                // If layer is specified, also move to that layer
                if (layer !== undefined) {
                    dispatch(
                        moveElementToLayer({
                            elementId: dragData.elementId,
                            screenId: screenId,
                            layer: layer,
                        }),
                    );
                }

                // Update selection
                dispatch(selectScreen(screenId));
                dispatch(selectElement(dragData.elementId));
            }
        } catch (error) {
            console.warn('Invalid drag data:', error);
        } finally {
            // Always reset drag state
            handleDragEnd();
        }
    };

    const handleGroupDrop = (
        e: React.DragEvent,
        groupId: string,
        screenId: string,
    ) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            const dragData = JSON.parse(
                e.dataTransfer.getData('application/json'),
            );

            if (dragData.type === 'element') {
                const { elementId, sourceScreenId } = dragData;

                // Move element to the group by updating its groupLocation
                // and clearing its individual grid positioning
                dispatch(
                    updateScreenUIElement({
                        screenId: sourceScreenId,
                        elementId,
                        key: 'groupLocation',
                        value: groupId,
                    }),
                );

                // Clear horizontal and vertical locations since element is now in a group
                dispatch(
                    updateScreenUIElement({
                        screenId: sourceScreenId,
                        elementId,
                        key: 'horizontalLocation',
                        value: undefined,
                    }),
                );

                dispatch(
                    updateScreenUIElement({
                        screenId: sourceScreenId,
                        elementId,
                        key: 'verticalLocation',
                        value: undefined,
                    }),
                );
            }
        } catch (error) {
            console.warn('Invalid drag data:', error);
        } finally {
            // Always reset drag state
            handleDragEnd();
        }
    };

    // Get all possible grid locations
    const getAllLocations = () => {
        const locations = [];
        const horizontalPositions = ['leading', 'center', 'trailing'];
        const verticalPositions = ['top', 'center', 'bottom'];

        for (const h of horizontalPositions) {
            for (const v of verticalPositions) {
                locations.push(`${h}-${v}`);
            }
        }
        return locations;
    };

    // Render empty location slot when dragging
    const renderEmptyLocation = (
        locationKey: string,
        screenId: string,
        layer: number,
    ) => {
        const locationLabel = LOCATION_LABELS[locationKey] || locationKey;

        return (
            <div
                key={`empty-${locationKey}`}
                className={`flex items-center gap-2 p-2 ml-12 rounded cursor-pointer transition-colors border-2 border-dashed ${
                    hierarchyState.dragHoverTarget ===
                        `location-${screenId}-${locationKey}` &&
                    hierarchyState.isDragging
                        ? 'bg-blue-200 border-blue-500'
                        : isDragFromPalette
                        ? 'bg-blue-100 border-blue-400 hover:bg-blue-200 hover:border-blue-500'
                        : 'bg-gray-50 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                }`}
                onDragOver={handleDragOver}
                onDragEnter={() =>
                    handleDragEnter(`location-${screenId}-${locationKey}`)
                }
                onDragLeave={handleDragLeave}
                onDrop={(e) =>
                    handleLocationDrop(e, screenId, locationKey, layer)
                }
                title={
                    isDragFromPalette
                        ? `Drop ${draggedElementType} here: ${locationLabel}`
                        : `Drop element here: ${locationLabel}`
                }
            >
                <Square className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500 italic">
                    Empty - {locationLabel}
                </span>
            </div>
        );
    };

    const renderElement = (
        element: UIElement,
        screenId: string,
        indentLevel: number = 0,
    ) => {
        const IconComponent = ELEMENT_ICONS[element.type] || Square;
        const isSelected =
            selectedElementId === element.id && selectedScreenId === screenId;

        // If this is a group element, we need to render it with its children
        if (element.type === 'group') {
            return renderGroupElement(element, screenId, indentLevel);
        }

        return (
            <div
                key={element.id}
                data-element-id={element.id}
                className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-all duration-200 group border-2 ${
                    isSelected
                        ? 'bg-blue-100 text-blue-800 border-blue-300 shadow-md scale-[1.02]'
                        : 'hover:bg-gray-100 text-gray-700 border-transparent hover:border-gray-200'
                }`}
                style={{ marginLeft: `${48 + indentLevel * 16}px` }}
                onClick={() => handleElementSelect(screenId, element.id)}
                onContextMenu={(e) =>
                    handleContextMenu(e, element.id, screenId)
                }
                draggable
                onDragStart={(e) => {
                    handleDragStart(element.id);
                    e.dataTransfer.setData(
                        'application/json',
                        JSON.stringify({
                            type: 'element',
                            elementId: element.id,
                            sourceScreenId: screenId,
                        }),
                    );
                }}
                onDragEnd={handleDragEnd}
            >
                <IconComponent
                    className={`w-4 h-4 flex-shrink-0 ${
                        isSelected ? 'text-blue-700' : ''
                    }`}
                />
                <span
                    className={`text-sm truncate flex-1 ${
                        isSelected ? 'font-semibold' : ''
                    }`}
                >
                    {element.id}
                </span>
                <span
                    className={`text-xs flex-shrink-0 ${
                        isSelected ? 'text-blue-600' : 'text-gray-500'
                    }`}
                >
                    {element.type}
                </span>
                <MoreVertical className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
        );
    };

    const renderGroupElement = (
        groupElement: UIElement,
        screenId: string,
        indentLevel: number = 0,
    ) => {
        const IconComponent = ELEMENT_ICONS[groupElement.type] || Square;
        const isSelected =
            selectedElementId === groupElement.id &&
            selectedScreenId === screenId;

        // Find children of this group
        const screen = screens.find((s) => s.id === screenId);
        const groupChildren =
            screen?.elements?.filter(
                (el) => el.groupLocation === groupElement.id,
            ) || [];

        const isExpanded =
            hierarchyState.expandedGroups[groupElement.id] ?? true;

        return (
            <div key={groupElement.id}>
                {/* Group element itself */}
                <div
                    data-element-id={groupElement.id}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-all duration-200 group border-2 ${
                        isSelected
                            ? 'bg-purple-100 text-purple-800 border-purple-300 shadow-md scale-[1.02]'
                            : 'hover:bg-gray-100 text-gray-700 border-transparent hover:border-gray-200'
                    }`}
                    style={{ marginLeft: `${48 + indentLevel * 16}px` }}
                    onClick={() =>
                        handleElementSelect(screenId, groupElement.id)
                    }
                    onContextMenu={(e) =>
                        handleContextMenu(e, groupElement.id, screenId)
                    }
                    draggable
                    onDragStart={(e) => {
                        handleDragStart(groupElement.id);
                        e.dataTransfer.setData(
                            'application/json',
                            JSON.stringify({
                                type: 'element',
                                elementId: groupElement.id,
                                sourceScreenId: screenId,
                            }),
                        );
                    }}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={(e) =>
                        handleGroupDrop(e, groupElement.id, screenId)
                    }
                >
                    {/* Expand/collapse button */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setHierarchyState((prev) => ({
                                ...prev,
                                expandedGroups: {
                                    ...prev.expandedGroups,
                                    [groupElement.id]: !isExpanded,
                                },
                            }));
                        }}
                        className="w-4 h-4 flex items-center justify-center hover:bg-gray-200 rounded"
                    >
                        {groupChildren.length > 0 ? (
                            isExpanded ? (
                                <ChevronDown className="w-3 h-3" />
                            ) : (
                                <ChevronRight className="w-3 h-3" />
                            )
                        ) : (
                            <div className="w-3 h-3" />
                        )}
                    </button>

                    <IconComponent
                        className={`w-4 h-4 flex-shrink-0 ${
                            isSelected ? 'text-purple-700' : 'text-purple-600'
                        }`}
                    />
                    <span
                        className={`text-sm truncate flex-1 ${
                            isSelected ? 'font-semibold' : ''
                        }`}
                    >
                        {groupElement.id}
                    </span>
                    <span className="text-xs text-gray-500">
                        ({groupChildren.length} items)
                    </span>
                    <MoreVertical className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>

                {/* Group children */}
                {isExpanded && groupChildren.length > 0 && (
                    <div className="space-y-1">
                        {groupChildren.map((child) =>
                            renderElement(child, screenId, indentLevel + 1),
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderLocation = (
        locationKey: string,
        elements: UIElement[],
        screenId: string,
        layer?: number,
    ) => {
        // Show location if it has elements OR if we're dragging (for drop targets)
        if (
            elements.length === 0 &&
            !hierarchyState.isDragging &&
            !isDragFromPalette
        )
            return null;

        const isExpanded = hierarchyState.expandedLocations.has(
            `${screenId}-${locationKey}`,
        );
        const locationLabel = LOCATION_LABELS[locationKey] || locationKey;

        return (
            <div key={locationKey}>
                <div
                    className={`flex items-center gap-2 p-2 ml-8 cursor-pointer transition-colors border-2 rounded ${
                        hierarchyState.dragHoverTarget ===
                            `location-${screenId}-${locationKey}` &&
                        hierarchyState.isDragging
                            ? 'bg-blue-200 border-blue-500'
                            : isDragFromPalette
                            ? 'bg-blue-50 border-blue-300 hover:bg-blue-100 hover:border-blue-400'
                            : 'hover:bg-gray-50 border-transparent hover:border-blue-200'
                    }`}
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleLocationExpansion(`${screenId}-${locationKey}`);
                    }}
                    onDragOver={handleDragOver}
                    onDragEnter={() =>
                        handleDragEnter(`location-${screenId}-${locationKey}`)
                    }
                    onDragLeave={handleDragLeave}
                    onDrop={(e) =>
                        handleLocationDrop(e, screenId, locationKey, layer)
                    }
                    title={
                        isDragFromPalette
                            ? `Drop ${draggedElementType} here: ${locationLabel}`
                            : 'Drop elements here to move them to this location'
                    }
                >
                    {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                    ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                    )}
                    <MapPin className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-gray-700">
                        {locationLabel}
                    </span>
                    <span className="text-xs text-gray-500">
                        ({elements.length})
                    </span>
                </div>
                {isExpanded && (
                    <div className="space-y-1">
                        {elements
                            .filter((element) => !element.groupLocation) // Only show non-grouped elements
                            .map((element) => renderElement(element, screenId))}
                        {/* Show empty slot when dragging and no elements in this location */}
                        {hierarchyState.isDragging &&
                            elements.length === 0 &&
                            renderEmptyLocation(
                                locationKey,
                                screenId,
                                layer || 0,
                            )}
                    </div>
                )}
            </div>
        );
    };

    const renderLayer = (
        layerNumber: number,
        locations: { [location: string]: UIElement[] },
        screenId: string,
    ) => {
        const layerKey = `${screenId}-layer-${layerNumber}`;
        const isExpanded = hierarchyState.expandedLayers.has(layerKey);
        const totalElements = Object.values(locations).reduce(
            (sum, elements) => sum + elements.length,
            0,
        );

        // Always show layers, even if empty, so users can drop elements into them
        // if (totalElements === 0) return null;

        const layerLabels = {
            0: 'Background Layer',
            1: 'Content Layer',
            2: 'Overlay Layer',
        };

        return (
            <div key={layerNumber}>
                <div
                    className="flex items-center gap-2 p-2 ml-4 hover:bg-gray-50 cursor-pointer transition-colors border-2 border-transparent hover:border-indigo-200 rounded"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleLayerDrop(e, screenId, layerNumber)}
                    title="Drop elements here to move them to this layer"
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleLayerExpansion(layerKey);
                    }}
                >
                    {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                    ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                    )}
                    <Layers className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-medium text-gray-700">
                        {layerLabels[layerNumber as keyof typeof layerLabels]} (
                        {layerNumber})
                    </span>
                    <span className="text-xs text-gray-500">
                        ({totalElements === 0 ? 'empty' : totalElements})
                    </span>
                </div>
                {isExpanded && (
                    <div className="space-y-1">
                        {/* Show existing locations with elements */}
                        {Object.entries(locations).map(
                            ([locationKey, elements]) =>
                                renderLocation(
                                    locationKey,
                                    elements,
                                    screenId,
                                    layerNumber,
                                ),
                        )}
                        {/* Show empty locations when dragging */}
                        {hierarchyState.isDragging &&
                            getAllLocations()
                                .filter(
                                    (locationKey) =>
                                        !locations[locationKey] ||
                                        locations[locationKey].length === 0,
                                )
                                .map((locationKey) =>
                                    renderEmptyLocation(
                                        locationKey,
                                        screenId,
                                        layerNumber,
                                    ),
                                )}
                        {/* Show message for completely empty layer */}
                        {totalElements === 0 && !hierarchyState.isDragging && (
                            <div className="p-2 ml-12 text-xs text-gray-400 italic">
                                This layer is empty. Drag elements here or start
                                dragging to see drop zones.
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderScreen = (screen: ScreenDefinition) => {
        const isExpanded = hierarchyState.expandedScreens.has(screen.id);
        const isSelected = selectedScreenId === screen.id && !selectedElementId;
        const hierarchy = getScreenHierarchy(screen);

        const stageColors = {
            attract: 'text-green-600',
            capture: 'text-blue-600',
            processing: 'text-yellow-600',
            review: 'text-purple-600',
            share: 'text-pink-600',
        };

        return (
            <div
                key={screen.id}
                className="border-b border-gray-100 last:border-b-0"
            >
                <div
                    className={`flex items-center gap-2 p-3 cursor-pointer transition-colors border-2 border-transparent hover:border-green-200 rounded ${
                        isSelected
                            ? 'bg-blue-100 text-blue-800'
                            : 'hover:bg-gray-50 text-gray-700'
                    }`}
                    onClick={() => handleScreenSelect(screen.id)}
                    onContextMenu={(e) => handleScreenContextMenu(e, screen.id)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleScreenDrop(e, screen.id)}
                    title="Drop elements here to move them to this screen"
                >
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleScreenExpansion(screen.id);
                        }}
                        className="p-0.5"
                    >
                        {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                    </button>
                    <Monitor
                        className={`w-4 h-4 ${
                            stageColors[
                                screen.stage?.type as keyof typeof stageColors
                            ] || 'text-gray-600'
                        }`}
                    />
                    <span className="text-sm font-medium flex-1 truncate">
                        {screen.id}
                    </span>
                    <span className="text-xs text-gray-500 flex-shrink-0">
                        {screen.stage?.type} • {screen.elements?.length || 0}
                    </span>
                </div>
                {isExpanded && (
                    <div className="pb-2">
                        {[0, 1, 2].map((layerNum) =>
                            renderLayer(
                                layerNum,
                                hierarchy[layerNum],
                                screen.id,
                            ),
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div
            className="w-80 bg-white border-r border-gray-200 flex flex-col h-full max-h-screen overflow-hidden overscroll-contain"
            onClick={hideContextMenu}
        >
            {/* Header */}
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    Experience Structure
                </h3>
                <p className="text-xs text-gray-600 mt-1">
                    Screens • Layers • Locations • Elements
                </p>
            </div>

            {/* Add Screen Button */}
            <div className="border-b border-gray-200 bg-white">
                <button
                    onClick={handleCreateScreen}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:border-blue-300 transition-colors"
                    style={{
                        width: 'calc(100% - 1rem)',
                    }}
                >
                    <Plus className="w-4 h-4" />
                    Add Screen
                </button>
            </div>

            {/* Tree Content */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 overscroll-contain">
                <div className="p-2">
                    {screens.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <Monitor className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                            <p className="text-sm">No screens created yet</p>
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {screens.map((screen) => renderScreen(screen))}
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-gray-200 bg-gray-50">
                <div className="text-xs text-gray-600">
                    <div className="flex items-center justify-between">
                        <span>{screens.length} screens</span>
                        <span>
                            {screens.reduce(
                                (sum, screen) =>
                                    sum + (screen.elements?.length || 0),
                                0,
                            )}{' '}
                            elements
                        </span>
                    </div>
                </div>
            </div>

            {/* Context Menu */}
            {hierarchyState.contextMenu.visible && (
                <div
                    ref={contextMenuRef}
                    className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-[9999] min-w-[180px]"
                    style={{
                        left: hierarchyState.contextMenu.x,
                        top: hierarchyState.contextMenu.y,
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {hierarchyState.contextMenu.elementId ? (
                        // Element context menu
                        <>
                            <button
                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                onClick={handleLayerUp}
                            >
                                <ArrowUp className="w-4 h-4" />
                                Push Layer Up
                            </button>
                            <button
                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                onClick={handleLayerDown}
                            >
                                <ArrowDown className="w-4 h-4" />
                                Push Layer Down
                            </button>
                            <div className="border-t border-gray-200 my-1"></div>
                            <button
                                className="w-full px-4 py-2 text-left text-sm hover:bg-red-100 hover:text-red-700 flex items-center gap-2"
                                onClick={handleDeleteElement}
                            >
                                <Trash2 className="w-4 h-4" />
                                Delete Element
                            </button>
                        </>
                    ) : (
                        // Screen context menu
                        <button
                            className="w-full px-4 py-2 text-left text-sm hover:bg-red-100 hover:text-red-700 flex items-center gap-2"
                            onClick={handleDeleteScreen}
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete Screen
                        </button>
                    )}
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                isOpen={deleteDialog.isOpen}
                onClose={() =>
                    setDeleteDialog({
                        isOpen: false,
                        type: 'element',
                        title: '',
                        message: '',
                    })
                }
                onConfirm={
                    deleteDialog.type === 'element'
                        ? confirmDeleteElement
                        : confirmDeleteScreen
                }
                title={deleteDialog.title}
                message={deleteDialog.message}
                confirmButtonText={
                    deleteDialog.type === 'element'
                        ? 'Delete Element'
                        : 'Delete Screen'
                }
            />
        </div>
    );
}
